/**
 * ============================================================================
 * TIME SLOT CALENDAR - PRODUCTION-READY COMPONENT
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS:
 *
 * 1. TANSTACK QUERY INTEGRATION
 *    - Uses TanStack Query for data fetching (NO manual fetch)
 *    - Parent component passes data via props (single source of truth)
 *    - No useEffect loops or manual state management
 *
 * 2. PERFORMANCE
 *    - Receives data from parent's cached query
 *    - No duplicate API calls per court
 *    - Lightweight component, fast rendering
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { Calendar, Clock, Lock, CheckCircle, RefreshCw, Globe, X } from 'lucide-react';

interface TimeSlot {
  time: string;
  start_time: string;
  end_time: string;
  status: 'available' | 'booked' | 'blocked' | 'unavailable';
  reference?: string | null;
  reason?: string | null;
  block_id?: number | null;
  partner_id?: number | null;
  partner_name?: string | null;
  is_partner_block?: boolean;
  blocked_by?: string | null;
}

interface ScheduleData {
  total_slots: number;
  available: number;
  booked: number;
  blocked: number;
  unavailable?: number;
  slots: TimeSlot[];
}

interface TimeSlotCalendarProps {
  courtId: number;
  courtName: string;
  venueId: number;
  scheduleData?: ScheduleData | null; // Data passed from parent
  isLoading?: boolean; // Loading state from parent
  onManageClick?: () => void;
  viewOnly?: boolean;
}

interface SlotDetails {
  time: string;
  endTime: string;
  status: 'available' | 'booked' | 'blocked' | 'unavailable';
  reference?: string | null;
  reason?: string | null;
  isPartnerBlock?: boolean;
  partnerName?: string | null;
  blockedBy?: string | null;
}

const TimeSlotCalendar: React.FC<TimeSlotCalendarProps> = ({
  courtId,
  courtName,
  venueId,
  scheduleData,
  isLoading = false,
  onManageClick,
  viewOnly = false,
}) => {
  const [selectedSlot, setSelectedSlot] = useState<SlotDetails | null>(null);

  const handleSlotClick = (slot: TimeSlot) => {
    const isPartner = slot.is_partner_block || slot.reason === 'third_party';
    setSelectedSlot({
      time: slot.start_time,
      endTime: slot.end_time,
      status: slot.status,
      reference: slot.reference,
      reason: slot.reason,
      isPartnerBlock: isPartner,
      partnerName: slot.partner_name,
      blockedBy: slot.blocked_by,
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!scheduleData || scheduleData.total_slots === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">{courtName}</h4>
          {!viewOnly && onManageClick && (
            <button
              onClick={onManageClick}
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
            >
              Manage →
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500">Venue closed today</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-gray-900">{courtName}</h4>
        {!viewOnly && onManageClick && (
          <button
            onClick={onManageClick}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
          >
            Manage →
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className={`grid gap-2 mb-3 ${(scheduleData.unavailable ?? 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <div className="bg-green-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="text-xs font-semibold text-green-900">
              {scheduleData.available} Available
            </span>
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <Calendar className="h-3 w-3 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900">
              {scheduleData.booked} Booked
            </span>
          </div>
        </div>
        {(scheduleData.unavailable ?? 0) > 0 && (
          <div className="bg-amber-50 rounded-lg p-2">
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3 text-amber-600" />
              <span className="text-xs font-semibold text-amber-900">
                {scheduleData.unavailable} In Use
              </span>
            </div>
          </div>
        )}
        <div className="bg-red-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <Lock className="h-3 w-3 text-red-600" />
            <span className="text-xs font-semibold text-red-900">
              {scheduleData.blocked} Blocked
            </span>
          </div>
        </div>
      </div>

      {/* Time Slots - horizontal scroll slider */}
      <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max pb-2">
          {scheduleData.slots.map((slot: TimeSlot, index: number) => {
            const isPartnerBlock = slot.status === 'blocked' && (slot.is_partner_block || slot.reason === 'third_party');
            return (
              <button
                key={index}
                onClick={() => handleSlotClick(slot)}
                className={`flex-shrink-0 w-16 rounded-lg p-2 border transition-all hover:opacity-80 ${
                  slot.status === 'available'
                    ? 'bg-green-50 border-green-200'
                    : slot.status === 'booked'
                    ? 'bg-blue-50 border-blue-200'
                    : slot.status === 'unavailable'
                    ? 'bg-amber-50 border-amber-200'
                    : isPartnerBlock
                    ? 'bg-teal-50 border-teal-200'
                    : 'bg-red-50 border-red-200'
                }`}
                title={
                  isPartnerBlock
                    ? `Third-Party: ${slot.partner_name || 'Partner Sync'}`
                    : slot.status === 'unavailable' && slot.reason
                    ? `In Use: ${slot.reason}`
                    : undefined
                }
              >
                <div className="text-xs font-bold text-gray-900 text-center">
                  {slot.start_time}
                </div>
                <div className="text-xs text-gray-600 text-center mt-0.5">
                  {slot.status === 'available' ? '✓' : slot.status === 'booked' ? '📅' : slot.status === 'unavailable' ? '⏸️' : isPartnerBlock ? '🔗' : '🔒'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot Details Modal */}
      {selectedSlot && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setSelectedSlot(null)}
        >
          <div
            className="bg-white rounded-xl p-5 w-[90%] max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-base font-bold text-gray-900">
                  {selectedSlot.time} - {selectedSlot.endTime}
                </h5>
                <p className="text-xs text-gray-500 mt-0.5">{courtName}</p>
              </div>
              <button
                onClick={() => setSelectedSlot(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Details */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span
                  className={`font-semibold ${
                    selectedSlot.status === 'available'
                      ? 'text-green-600'
                      : selectedSlot.status === 'booked'
                      ? 'text-blue-600'
                      : selectedSlot.status === 'unavailable'
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                >
                  {selectedSlot.status === 'available'
                    ? 'Available'
                    : selectedSlot.status === 'booked'
                    ? 'Booked'
                    : selectedSlot.status === 'unavailable'
                    ? 'In Use'
                    : 'Blocked'}
                </span>
              </div>
              {selectedSlot.reference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference</span>
                  <span className="font-semibold text-gray-900">{selectedSlot.reference}</span>
                </div>
              )}
              {selectedSlot.isPartnerBlock && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Source</span>
                  <span className="font-semibold text-teal-600">{selectedSlot.partnerName || 'Partner Sync'}</span>
                </div>
              )}
              {selectedSlot.blockedBy && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Blocked By</span>
                  <span className="font-semibold text-red-600">{selectedSlot.blockedBy}</span>
                </div>
              )}
              {selectedSlot.status === 'unavailable' && selectedSlot.reason && (
                <div className="flex justify-between">
                  <span className="text-gray-600">In Use By</span>
                  <span className="font-semibold text-amber-600">{selectedSlot.reason}</span>
                </div>
              )}
              {selectedSlot.status === 'blocked' && selectedSlot.reason && !selectedSlot.isPartnerBlock && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reason</span>
                  <span className="font-semibold text-red-600 capitalize">{selectedSlot.reason.replace(/_/g, ' ')}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => setSelectedSlot(null)}
              className="w-full mt-4 bg-gray-900 text-white py-2.5 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotCalendar;
